import { TaxonomicGame } from './game.js';
import { TaxonomicUI } from './ui.js';
import { TaxonomicStorage } from './storage.js';
import { deepDiveRouter } from './deepDiveRouterInstance.js';
import { DarkModeController } from './darkMode.js';
import { ensureFocusIndicators } from './accessibility.js';
import audioEngine from './audioEngine.js';
import { getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { initInstrumentation, instrumentation } from './instrumentation.js';
import { GameTimer } from './timer.js';
import { PuzzleCalendar } from './calendar.js';
import { consoleCapture } from './consoleCapture.js';
import { SettingsDrawer } from './settingsDrawer.js';
import { BugReportManager } from './bugReport.js';

// Start console capture early to catch initialization logs
consoleCapture.start();

// Global timer variable
let gameTimer = null;

// Initialize Grafana Faro for observability
initializeFaro({
    url: 'https://faro-collector-prod-us-central-0.grafana.net/collect/e56335c32471ded704613bcf826ab7aa',
    app: {
        name: 'Taxonomic',
        version: '1.0.0',
        environment: 'production'
    },
    instrumentations: [
        // Mandatory, omits default instrumentations otherwise.
        ...getWebInstrumentations(),

        // Tracing package to get end-to-end visibility for HTTP requests.
        new TracingInstrumentation(),
    ],
});

// Initialize instrumentation singleton
initInstrumentation(window.faro);

async function initGame() {
    try {
        // Initialize dark mode
        const darkMode = new DarkModeController();
        darkMode.init();

        // Initialize settings drawer
        const settingsDrawer = new SettingsDrawer();

        // Wire settings button
        document.getElementById('settings-btn')?.addEventListener('click', () => {
            settingsDrawer.toggle();
        });

        // Set up drawer toggles
        const drawerThemeToggle = document.getElementById('drawer-theme-toggle');
        if (drawerThemeToggle) {
            // Set initial state
            if (darkMode.getTheme() === 'dark') {
                drawerThemeToggle.classList.add('active');
            }
            drawerThemeToggle.addEventListener('click', () => {
                darkMode.toggle();
                drawerThemeToggle.classList.toggle('active');
            });
        }

        const drawerMusicToggle = document.getElementById('drawer-music-toggle');
        if (drawerMusicToggle) {
            if (!audioEngine.isMusicMuted) {
                drawerMusicToggle.classList.add('active');
            }
            drawerMusicToggle.addEventListener('click', () => {
                const isOn = audioEngine.toggleMusic();
                drawerMusicToggle.classList.toggle('active', isOn);
                const announcer = document.getElementById('announcer');
                if (announcer) {
                    announcer.textContent = isOn ? 'Music enabled' : 'Music muted';
                }
            });
        }

        const drawerSfxToggle = document.getElementById('drawer-sfx-toggle');
        if (drawerSfxToggle) {
            if (!audioEngine.isSfxMuted) {
                drawerSfxToggle.classList.add('active');
            }
            drawerSfxToggle.addEventListener('click', () => {
                const isOn = audioEngine.toggleSfx();
                drawerSfxToggle.classList.toggle('active', isOn);
                const announcer = document.getElementById('announcer');
                if (announcer) {
                    announcer.textContent = isOn ? 'Sound effects enabled' : 'Sound effects muted';
                }
            });
        }

        // Ensure focus indicators are present
        ensureFocusIndicators();

        // Clear old states
        TaxonomicStorage.clearOldStates();

        const game = new TaxonomicGame();
        await game.init();

        // Try to load saved state
        const savedState = TaxonomicStorage.loadState(game.state.puzzleDate, game.getVisualGridSize());
        if (savedState) {
            // Restore state
            game.state.mode = savedState.mode;
            game.state.selectedWords = savedState.selectedWords;
            game.state.pairedWords = savedState.pairedWords;
            game.state.completedCategories = savedState.completedCategories;
            game.state.deepDiveContext = savedState.deepDiveContext;

            // Restore shuffledWords to preserve exact word positions
            if (savedState.shuffledWords) {
                game.state.shuffledWords = savedState.shuffledWords;
            }

            // Restore mainBoardState to preserve incomplete pairs
            if (savedState.mainBoardState) {
                game.state.mainBoardState = savedState.mainBoardState;
            }

            // Restore stackedPairs for matched pair stacks feature
            game.state.stackedPairs = savedState.stackedPairs || {};

            // If in deep dive mode, mark for mini-game recreation
            // (Mini-game instances can't be serialized to localStorage)
            // We'll recreate it after ui is created so we can set up the callback properly

            // Initialize audio progression from saved state
            audioEngine.setProgression(game.state.completedCategories.length);
        }

        // Initialize timer from saved state or fresh
        const savedTimerState = savedState?.timerState;
        gameTimer = new GameTimer(savedTimerState);

        // Only start timer if game is not already complete
        if (!game.isGameComplete()) {
            gameTimer.start();
        }

        const ui = new TaxonomicUI(game);

        // Initialize bug report manager
        const bugReportManager = new BugReportManager(window.faro, game, consoleCapture);

        // Wire bug report buttons
        document.getElementById('bug-report-btn')?.addEventListener('click', () => {
            bugReportManager.showModal();
        });

        document.getElementById('footer-bug-report')?.addEventListener('click', (e) => {
            e.preventDefault();
            bugReportManager.showModal();
        });

        // Now recreate mini-game if we were in deep dive mode
        if (savedState && savedState.mode === 'deepdive' && game.state.deepDiveContext) {
            const categoryId = game.state.deepDiveContext.categoryId;
            const category = game.state.currentPuzzle.categories.find(c => c.id === categoryId);

            if (category) {
                // Create callback for when mini-game completes
                const onMiniGameComplete = () => {
                    const exitResult = game.exitDeepDive();
                    if (exitResult.action === 'game_complete') {
                        ui.showVictory();
                    } else {
                        ui.render();
                    }
                };

                // Recreate mini-game instance with completion callback
                const miniGame = deepDiveRouter.loadMiniGame(category, onMiniGameComplete);
                game.state.deepDiveContext.miniGame = miniGame;
            } else {
                // Category not found in current puzzle - reset to main mode
                console.warn(`Saved deep dive category "${categoryId}" not found in today's puzzle, resetting to main`);
                game.state.mode = 'main';
                game.state.deepDiveContext = null;
                TaxonomicStorage.saveState(game);
            }
        }

        // Helper to save state with timer
        const saveStateWithTimer = () => {
            game.state.timerState = gameTimer?.getState();
            TaxonomicStorage.saveState(game);
        };

        // Save state on every action
        const originalSelectWord = game.selectWord.bind(game);
        game.selectWord = function(index) {
            const result = originalSelectWord(index);
            saveStateWithTimer();
            return result;
        };

        // Also save state when entering/exiting deep dive
        const originalEnterDeepDive = game.enterDeepDive.bind(game);
        game.enterDeepDive = function(categoryId, onComplete) {
            const result = originalEnterDeepDive(categoryId, onComplete);
            saveStateWithTimer();

            // Update audio progression based on completed categories
            audioEngine.setProgression(game.state.completedCategories.length);

            return result;
        };

        const originalExitDeepDive = game.exitDeepDive.bind(game);
        game.exitDeepDive = function() {
            const result = originalExitDeepDive();
            saveStateWithTimer();
            return result;
        };

        // Update date display
        document.getElementById('puzzle-date').textContent = game.state.puzzleDate;

        // Check if game is already complete and show victory
        if (game.isGameComplete()) {
            ui.showVictory();
        } else {
            // Render initial board
            ui.render();
        }

        // Retry button handler
        document.getElementById('retry-button').addEventListener('click', () => {
            location.reload();
        });

        // Pause timer when tab is hidden
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                gameTimer?.pause();
            } else if (!game.isGameComplete()) {
                gameTimer?.start();
            }
        });

        // Pause timer when window loses focus
        window.addEventListener('blur', () => gameTimer?.pause());
        window.addEventListener('focus', () => {
            if (!game.isGameComplete()) {
                gameTimer?.start();
            }
        });

        // Initialize calendar
        const handleDateSelect = (dateStr, isCompleted) => {
            ui.hideCalendar();

            if (isCompleted) {
                const completion = TaxonomicStorage.getCompletionData(dateStr);
                // Load the puzzle for that date to show its categories
                const puzzleForDate = TaxonomicStorage.loadState(dateStr);
                if (puzzleForDate && game.state.currentPuzzle) {
                    ui.renderVictory(game.state.currentPuzzle, dateStr, {
                        completionTime: completion?.completionTime
                    });
                    ui.victoryScreen.classList.remove('hidden');
                } else {
                    // If we can't load the puzzle data, just redirect
                    window.location.href = `/${dateStr}`;
                }
            } else {
                // Load that puzzle
                window.location.href = `/${dateStr}`;
            }
        };

        const calendar = new PuzzleCalendar(handleDateSelect);
        await calendar.init();
        ui.calendar = calendar;

        // Wire calendar button
        document.getElementById('calendar-btn')?.addEventListener('click', () => {
            ui.showCalendar();
        });

        // Wire replay handler
        ui.onReplayPuzzle = (dateStr) => {
            // Clear saved state for the puzzle
            const key = TaxonomicStorage.STORAGE_KEY_PREFIX + dateStr;
            localStorage.removeItem(key);
            window.location.href = `/${dateStr}`;
        };

        // Override showVictory to record completion time
        const originalShowVictory = ui.showVictory.bind(ui);
        ui.showVictory = function() {
            gameTimer?.pause();
            const completionTime = gameTimer?.getElapsedTime() || 0;

            // Mark puzzle as completed with time
            TaxonomicStorage.markCompleted(game.state.puzzleDate, completionTime);

            // Update game state for storage
            game.state.completionStatus = 'completed';
            game.state.completionTime = completionTime;
            game.state.completedAt = Date.now();
            game.state.timerState = gameTimer?.getState();
            TaxonomicStorage.saveState(game);

            // Call original victory display
            originalShowVictory();
        };

    } catch (error) {
        console.error('Failed to initialize game:', error);

        // Track initialization error
        instrumentation?.trackError(error, {
            operation: 'initGame'
        });

        const errorScreen = document.getElementById('error-screen');
        const errorMessage = document.getElementById('error-message');
        errorMessage.textContent = 'No puzzle available yet. Check back soon!';
        errorScreen.classList.remove('hidden');
    }
}

// Initialize after page fully loads (including stylesheets)
// This prevents layout calculations before styles are applied
if (document.readyState === 'complete') {
    initGame();
} else {
    window.addEventListener('load', initGame);
}
